import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Button, Chip, Modal, ModalSize } from '@column/column-ui-kit';

import { useQueryParams } from '~/hooks/useQueryParams';
import { Invite } from '~/repositories/InviteRepository';
import { getRoleLabel } from '~/repositories/RoleRepository';
import { UserRepository } from '~/repositories/UserRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Paragraph } from '~/styles';

interface InviteModalState {
  invite: Invite;
  inviteCode?: string;
}

const StyledBody = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  text-align: center;
  vertical-align: middle;

  & > * {
    margin-right: 6px;
  }
`;

export const AcceptPlatformInviteModal: React.FC = () => {
  const { currentUser } = useSessionStore();
  const { queryParams, removeQueryParam } = useQueryParams<{ code?: string; invite?: string }>();
  const [inviteState, setInviteState] = useState<InviteModalState>();
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const addSuccessNotification = useNotificationStore((s) => s.addSuccessNotification);
  const { refetchCurrentUser } = useSessionStore();
  const queryClient = useQueryClient();
  const { data: invitesData, refetch: refetchInvites } = useQuery({
    queryKey: ['platformInvites', currentUser?.id],
    queryFn: () => UserRepository.getInvites({ dashboardUserId: currentUser!.id }),
    enabled: !!currentUser?.id,
  });

  const acceptInviteMutation = useMutation({
    mutationFn: ({ inviteCode, platformId }: { inviteCode: string; platformId: string }) =>
      UserRepository.acceptInvite({ inviteCode, platformId }),
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Something went wrong, please try again.';
      addDangerNotification({
        content: message,
        display: 'popup',
      });
    },
    onSuccess: () => {
      addSuccessNotification({
        content: 'Invite accepted',
      });
      refetchCurrentUser();
      queryClient.invalidateQueries({ queryKey: ['platformInvites', currentUser?.id] });
      closeModal();
    },
  });

  const inviteParam = queryParams?.invite;
  const inviteCodeParam = queryParams?.code;

  useEffect(() => {
    if (!inviteParam) {
      setInviteState(undefined);
      return;
    }

    if (!currentUser?.id) {
      return;
    }

    const matchedInvite = invitesData?.invites.find((entry) => entry.platformId === inviteParam);
    if (matchedInvite) {
      setInviteState({ invite: matchedInvite, inviteCode: inviteCodeParam });
    } else if (invitesData) {
      setInviteState(undefined);
    }
  }, [currentUser?.id, inviteCodeParam, inviteParam, invitesData]);

  useEffect(() => {
    if (!inviteParam || !currentUser?.id) {
      return;
    }

    refetchInvites();
  }, [currentUser?.id, inviteParam, refetchInvites]);

  const closeModal = useCallback(() => {
    setInviteState(undefined);
    acceptInviteMutation.reset();
    removeQueryParam('code');
    removeQueryParam('invite');
  }, [acceptInviteMutation, removeQueryParam]);

  const roleName = useMemo(() => {
    if (!inviteState?.invite) return '';
    return getRoleLabel(inviteState.invite.platformRole);
  }, [inviteState?.invite]);

  const handleAcceptClick = useCallback(() => {
    if (!inviteState?.invite || !inviteState?.inviteCode) {
      return;
    }

    acceptInviteMutation.mutate({ inviteCode: inviteState.inviteCode, platformId: inviteState.invite.platformId });
  }, [acceptInviteMutation, inviteState?.invite, inviteState?.inviteCode]);

  return (
    <Modal isOpen={!!inviteState?.invite} onClose={closeModal} size={ModalSize.Small}>
      <Modal.IconCircle type="primary" icon="AnimationMailbox" />
      <Modal.Content>
        <Modal.Headline fullWidth>Join {inviteState?.invite?.platformName}?</Modal.Headline>
        <StyledBody>
          <Paragraph>Your role will be set as </Paragraph>
          <Chip>{roleName}</Chip>
        </StyledBody>
      </Modal.Content>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          onClick={handleAcceptClick}
          isLoading={acceptInviteMutation.isPending}
          isDisabled={!inviteState?.invite || acceptInviteMutation.isPending}
        >
          Accept
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
